/*
 * Copyright 2026 Sony Corporation
 */
import { Box, CircularProgress, IconButton, Paper, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { settled, type Todo, type TodoState } from '../services/todo';

import { ChevronIcon } from './icons';

const COLOR: Record<Exclude<TodoState, 'active'>, string> = {
  done: 'success.main',
  pending: 'text.disabled',
  failed: 'error.main',
  skipped: 'text.disabled',
};

const SYMBOL: Record<Exclude<TodoState, 'active'>, string> = { done: '✓', pending: '', failed: '!', skipped: '–' };

const StateIcon = ({ state }: { state: TodoState }) =>
  state === 'active' ? (
    <CircularProgress size={18} thickness={5} aria-label="in progress" />
  ) : (
    <Box
      aria-label={state}
      role="img"
      sx={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'grid',
        placeItems: 'center',
        fontSize: 12,
        lineHeight: 1,
        border: 2,
        borderColor: COLOR[state],
        ...(state === 'done' || state === 'failed' ? { bgcolor: COLOR[state], color: 'common.black' } : {}),
        ...(state === 'skipped' ? { color: COLOR[state] } : {}),
      }}
    >
      {SYMBOL[state]}
    </Box>
  );

export interface TodoPanelProps {
  items: Todo[];
}

/** The stages of the latest request and where it stands, so progress can be seen without reading the chat. */
export const TodoPanel = ({ items }: TodoPanelProps) => {
  const [open, setOpen] = useState(true);

  return (
    <Paper variant="outlined" component="section" aria-label="Todos" sx={{ p: 2 }}>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle1">
          Todos ({settled(items)}/{items.length})
        </Typography>
        <IconButton size="small" aria-label={open ? 'Hide todos' : 'Show todos'} onClick={() => setOpen(!open)}>
          <ChevronIcon direction={open ? 'up' : 'down'} fontSize="small" />
        </IconButton>
      </Stack>
      {open && (
        <Stack component="ol" spacing={1.25} sx={{ listStyle: 'none', p: 0, m: 0, mt: 1.5 }}>
          {items.map((item) => (
            <Stack
              component="li"
              key={item.id}
              direction="row"
              spacing={1.25}
              sx={{ alignItems: 'flex-start' }}
              aria-current={item.state === 'active' ? 'step' : undefined}
            >
              <Box sx={{ pt: 0.25 }}>
                <StateIcon state={item.state} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: item.state === 'pending' || item.state === 'skipped' ? 'text.secondary' : 'text.primary',
                    fontWeight: item.state === 'active' ? 700 : 400,
                  }}
                >
                  {item.label}
                </Typography>
                {item.detail && (
                  <Typography
                    variant="caption"
                    sx={{ color: item.state === 'failed' ? 'error.light' : 'text.secondary', overflowWrap: 'anywhere' }}
                  >
                    {item.detail}
                  </Typography>
                )}
              </Box>
            </Stack>
          ))}
        </Stack>
      )}
    </Paper>
  );
};
